/**
 * views/Settings/RoadDistanceHealth.js
 * Settings > Delivery: is delivery priced on Google road distance, or on the
 * straight-line estimate? Reads GET /api/delivery-fee/distance-health, which
 * measures one Kampala trip live (Google Routes API, then the legacy Distance
 * Matrix, then the straight line, as every quote does) and counts the last 24
 * hours of quotes. Added 3 Nov 2026, when every production quote was found on
 * the straight line because Google refused the server's key. Everything shown
 * is the server's answer; nothing is worked out here.
 */
import React, { useCallback, useEffect, useState } from "react";
import { apiGetDistanceHealth } from "../../api/index.js";
import AppButton from "../../components/ui/AppButton";

export const PROVIDER_NAMES = Object.freeze({
  google_routes: "Google Routes API",
  google_maps: "Google Distance Matrix (legacy)",
  haversine: "the straight-line estimate",
});

const providerName = (provider) => PROVIDER_NAMES[provider] || provider || "unknown";

/** Google's refusal in one phrase: its status, then its own words. */
export const refusalText = (attempt) =>
  [attempt?.status, attempt?.message].filter(Boolean).join(": ") || "no reason given";

/**
 * The headline and its tone, from the server's answer.
 * @returns {{ tone: "ok"|"warn", headline: string, detail: string|null }}
 */
export const roadDistanceStatus = (health) => {
  const refusals = (health?.probe?.attempts || []).filter((attempt) => !attempt.ok);
  if (health?.roadDistanceWorking) {
    return {
      tone: "ok",
      headline: "Road distance: Google working",
      detail:
        health.provider === "google_maps" && refusals[0]
          ? `Answered by the legacy Google Distance Matrix. The Routes API refused (${refusalText(refusals[0])}); enable the Routes API for the server key.`
          : `Answered by ${providerName(health.provider)}.`,
    };
  }
  const reason = health?.keyConfigured === false ? "no Google key on the server" : refusalText(refusals[0]);
  return {
    tone: "warn",
    headline: `Road distance: falling back to straight line (${reason})`,
    detail: "Delivery fees are priced on the straight line times the road factor below until Google answers.",
  };
};

const percent = (rate) => (rate === null || rate === undefined ? null : `${Math.round(rate * 1000) / 10}%`);

/** The last 24 hours in one sentence. */
export const windowText = (window) => {
  if (!window || !window.measured) return "No distances were measured in the last 24 hours.";
  const n = window.measured;
  return `Last 24 hours: ${n.toLocaleString()} ${n === 1 ? "distance" : "distances"} measured, ` +
    `${window.fallbacks.toLocaleString()} on the straight-line estimate (${percent(window.fallbackRate)}).`;
};

const TONE = {
  ok: { bg: "#eef7f1", border: "#cfe6d8", ink: "#0a5c40" },
  warn: { bg: "#fdf1ec", border: "#f4c7b8", ink: "#8a2b06" },
};

export default function RoadDistanceHealth() {
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState(false);
  const [testing, setTesting] = useState(false);

  const runTest = useCallback(async () => {
    setTesting(true);
    try {
      const res = await apiGetDistanceHealth();
      setHealth(res.data?.success ? res.data : null);
      setFailed(!res.data?.success);
    } catch {
      setFailed(true);
    } finally {
      setTesting(false);
    }
  }, []);

  useEffect(() => { runTest(); }, [runTest]);

  const status = health ? roadDistanceStatus(health) : null;
  const tone = TONE[status?.tone || "warn"];
  const reasons = Object.entries(health?.last24h?.reasons || {}).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div data-testid="road-distance-health">
      {!health && !failed && <p style={{ fontSize: 13, color: "#66787b", margin: "0 0 10px" }}>Checking road distance with Google...</p>}
      {failed && !health && (
        <p role="alert" style={{ fontSize: 13, color: TONE.warn.ink, margin: "0 0 10px" }}>
          Could not check road distance. Try again.
        </p>
      )}
      {status && (
        <div role="status" style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.ink, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{status.headline}</div>
          {status.detail && <div style={{ fontSize: 12.5, marginTop: 4 }}>{status.detail}</div>}
        </div>
      )}
      {health && (
        <div style={{ fontSize: 12.5, color: "#33454b", lineHeight: 1.5 }}>
          <div>{windowText(health.last24h)}</div>
          {reasons.length > 0 && (
            <div>
              Google refusals:{" "}
              {reasons.map(([key, n]) => {
                const [provider, statusCode] = key.split(":");
                return `${providerName(provider)} ${statusCode} (${n.toLocaleString()})`;
              }).join(", ")}.
            </div>
          )}
          {health.probe && (
            <div>
              Live test at {new Date(health.probe.at).toLocaleTimeString()}: {Number(health.probe.distanceKm).toLocaleString()} km
              from the Lusuku origin to the city centre, by {providerName(health.provider)}.
            </div>
          )}
          <div>
            {health.keyConfigured
              ? `Google key read from ${health.keyEnvName}.`
              : "No Google key is set. Set GOOGLE_MAPS_SERVER_API_KEY on the server."}
          </div>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <AppButton onClick={runTest} loading={testing}>Test now</AppButton>
      </div>
    </div>
  );
}
